import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Container, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import loyaltyService from '../../services/loyaltyService';
import RecordSection from './RecordSection';
import SECTION_TITLES, { RECORD_SECTIONS } from './sectionTitles';

/**
 * Every record in one section, e.g. all of a member's visits, reached from the section's "See all" on the membership
 * page. The list is whatever the company allows (its "last N" or "all" setting, at most 200 rows from the server).
 */
const RecordsPage = () => {
  const { companyId: groupId, sectionKey } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Clear the last membership so its title never shows over another's while this one loads.
    setGroup(null);
    setError(null);
    loyaltyService
      .getGroupDetails(groupId)
      .then((response) => {
        if (!cancelled) setGroup(response.group);
      })
      .catch(() => {
        if (!cancelled) setError('This membership could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  // Only a member sees records, and only for a record section the company has switched on. The order also lists the
  // built-in sections (balance, offers, store locations) as `{}`, which have no records page, so the key is checked too.
  const section =
    group?.member_info?.is_member && RECORD_SECTIONS.includes(sectionKey) ? group.portal_records?.sections?.[sectionKey] : null;

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <IconButton onClick={() => navigate(`/memberships/${groupId}`)} sx={{ mb: 2 }} aria-label="Back to membership">
          <ArrowBackIcon />
        </IconButton>

        {!group && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {group && (
          <>
            <Typography variant="h4" component="h1">
              {SECTION_TITLES[sectionKey] || 'Records'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {group.name}
            </Typography>
            {section ? (
              <RecordSection groupId={groupId} sectionKey={sectionKey} section={section} showTitle={false} />
            ) : (
              <Alert severity="info" sx={{ mt: 3 }}>
                There is nothing to show here.
              </Alert>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default RecordsPage;
